import { CheckCircle2 } from "lucide-react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

interface SectionCardProps {
  kicker: string
  title: string
  subtitle?: string
  roleBadge?: string
  actions?: ReactNode
  children: ReactNode
  state?: "default" | "saved"
}

export function SectionCard({
  kicker,
  title,
  subtitle,
  roleBadge,
  actions,
  children,
  state = "default",
}: SectionCardProps) {
  const { t } = useTranslation("intake")
  const saved = state === "saved"
  return (
    <Card>
      <CardHeader>
        <div className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
          {kicker}
        </div>
        <CardTitle className="text-lg">{title}</CardTitle>
        {subtitle ? <CardDescription>{subtitle}</CardDescription> : null}
        {actions || roleBadge || saved ? (
          <CardAction className="flex items-center gap-2">
            {actions}
            {saved ? (
              <Badge
                variant="outline"
                className="inline-flex items-center gap-1 border-emerald-200 bg-emerald-50 text-[10px] tracking-wide text-emerald-700 uppercase dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
              >
                <CheckCircle2 className="size-3" />
                {t("sectionState.saved")}
              </Badge>
            ) : null}
            {roleBadge ? (
              <Badge
                variant="outline"
                className="text-[10px] tracking-wide uppercase"
              >
                {roleBadge}
              </Badge>
            ) : null}
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}
